import React, { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  ArrowLeft, 
  Calculator, 
  Zap, 
  Shield,
  Star,
  Clock,
  CreditCard,
  DollarSign
} from "lucide-react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useContext(AuthContext);
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingPackage, setProcessingPackage] = useState(null);

  useEffect(() => {
    fetchPricingData();
  }, []);

  const fetchPricingData = async () => {
    try {
      const response = await axios.get(`${API}/pricing/structure`);
      setPricingData(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error("Failed to load pricing information");
    }
  };

  const handlePurchaseCredits = async (packageType) => {
    if (loading) return;
    
    setLoading(true);
    setProcessingPackage(packageType);

    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        package_type: packageType
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Credit purchase error:', error);
      toast.error("Failed to start credit purchase process");
      setLoading(false);
      setProcessingPackage(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!pricingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading pricing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              
              <div className="text-sm text-slate-600">
                Welcome, {user?.username}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 bg-green-50 border-green-200 text-green-700">
            💰 Pay Only For What You Use
          </Badge>
          
          <h1 className="text-5xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Choose Your 
            <br />
            <span className="text-blue-600">Credit Package</span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            No monthly subscriptions. Buy credits and use them whenever you need to process bank statements.
          </p>
        </div>

        {/* Current Balance */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 max-w-2xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <CreditCard className="w-8 h-8 text-blue-500" />
              <div>
                <h3 className="text-2xl font-semibold text-slate-800">
                  Current Balance: {formatCurrency(user?.credit_balance || 0)}
                </h3>
                <p className="text-slate-600">Available processing credits</p>
              </div>
            </div>
            
            {user?.credit_balance > 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">
                  You have credits available! Each PDF costs ${pricingData.pay_per_use.base_upload_fee} + 
                  ${pricingData.pay_per_use.per_page_fee} per page.
                </p>
              </div>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-700">
                  You need credits to process PDF bank statements. Choose a package below to get started.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Pay Per Use Explanation */}
        <div className="bg-blue-50 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-blue-800 mb-2">How Pay-Per-Use Works</h3>
            <p className="text-blue-700">Simple, transparent pricing with no surprises</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-semibold text-blue-800 mb-2">
                ${pricingData.pay_per_use.base_upload_fee} Base Fee
              </h4>
              <p className="text-blue-700 text-sm">Per PDF upload, regardless of size</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">+</span>
              </div>
              <h4 className="font-semibold text-blue-800 mb-2">
                ${pricingData.pay_per_use.per_page_fee} Per Page
              </h4>
              <p className="text-blue-700 text-sm">For each page processed</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-semibold text-green-800 mb-2">Example: 5 Pages</h4>
              <p className="text-green-700 text-sm">$1 + $5 = $6 total cost</p>
            </div>
          </div>
        </div>

        {/* Credit Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingData.credit_packages && Object.entries(pricingData.credit_packages).map(([packageId, pkg]) => (
            <Card key={packageId} className={`relative border-2 shadow-xl transition-all duration-300 ${
              packageId === 'professional' 
                ? "border-blue-500 scale-105" 
                : "border-slate-200 hover:border-blue-300"
            }`}>
              {packageId === 'professional' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white px-4 py-1 flex items-center">
                    <Star className="w-4 h-4 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                  packageId === 'professional' ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-green-100'
                }`}>
                  <Zap className={`w-8 h-8 ${packageId === 'professional' ? 'text-white' : 'text-green-600'}`} />
                </div>
                <CardTitle className="text-2xl mb-2">{pkg.name}</CardTitle>
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl font-bold text-slate-800">{formatCurrency(pkg.price)}</span>
                    <div className="text-left">
                      <div className="text-sm text-slate-500 line-through">
                        {formatCurrency(pkg.credits * pricingData.pay_per_use.base_upload_fee)}
                      </div>
                      <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700 text-xs">
                        {pkg.savings}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm mt-2">{pkg.credits} processing credits</p>
                </div>
                <CardDescription className="text-center">
                  {pkg.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="px-6 pb-8">
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-2">Enough for approximately:</p>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="font-bold text-slate-800">{Math.floor(pkg.credits / 2)} statements</div>
                        <div className="text-slate-500">1-page each</div>
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{Math.floor(pkg.credits / 6)} statements</div>
                        <div className="text-slate-500">5-page each</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-slate-700">{pkg.credits} processing credits</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-slate-700">Credits never expire</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-slate-700">Full tax categorization</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-slate-700">Schedule C integration</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-slate-700">Excel export included</span>
                  </li>
                </ul>
                
                <Button 
                  size="lg" 
                  className={`w-full py-3 ${
                    packageId === 'professional' 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                  onClick={() => handlePurchaseCredits(packageId)}
                  disabled={loading}
                  data-testid={`${packageId}-purchase-btn`}
                >
                  {processingPackage === packageId ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    `Buy ${pkg.credits} Credits`
                  )}
                </Button>
                
                <p className="text-xs text-slate-500 text-center mt-3">
                  {formatCurrency(pkg.price / pkg.credits)} per credit
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-slate-800 text-center mb-8">
            Why Choose Credit Packages?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Save Money</h4>
              <p className="text-slate-600">Get up to 25% discount with bulk credit packages compared to individual processing.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Never Expire</h4>
              <p className="text-slate-600">Your credits are always available when you need them. No monthly fees or expiration dates.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="font-semibold text-slate-800 mb-2">Instant Processing</h4>
              <p className="text-slate-600">Process your bank statements immediately without waiting for subscription activation.</p>
            </div>
          </div>
        </div>

        {/* FAQ or Support */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">
            Questions about our pay-per-use pricing? Need help estimating costs?
          </p>
          <Button variant="outline" className="text-blue-600 border-blue-600 hover:bg-blue-50">
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;